import { useEffect, useState } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Check, Loader2, MapPin, Share2 } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'
import {
  fetchSharedLocations,
  importSharedLocations,
} from '@/data/share'
import { triggerNoaaForecastFetch } from '@/data/forecast'
import { useUser } from '@/hooks/useUser'

export const Route = createFileRoute(
  '/_authenticated/share/locations/$code',
)({
  component: ShareLocationConsumerPage,
})

function ShareLocationConsumerPage() {
  const { code } = Route.useParams()
  const router = useRouter()
  const { user } = useUser()
  const queryClient = useQueryClient()

  const { data: sharedData } = useQuery({
    queryKey: ['shared-locations', code],
    queryFn: () => fetchSharedLocations(code),
  })

  const [selectedLocations, setSelectedLocations] = useState<Set<string>>(
    new Set(),
  )
  const [nameOverrides, setNameOverrides] = useState<Record<string, string>>({})
  const [imported, setImported] = useState(false)

  // Initialize selection with all locations
  useEffect(() => {
    if (sharedData?.customLocations) {
      setSelectedLocations(
        new Set(sharedData.customLocations.map((loc) => loc.name)),
      )
    }
  }, [sharedData])

  // Import mutation
  const importMutation = useMutation<
    { imported: any[]; total: number; grids: { id: string; full_grid_id: string }[] },
    Error,
    { locationNames: string[]; nameOverrides: Record<string, string> }
  >({
    mutationFn: async (data) =>
      await importSharedLocations({
        shareCode: code,
        locationNames: data.locationNames,
        nameOverrides: data.nameOverrides,
      }),
    onSuccess: async (result) => {
      if (result.imported.length > 0) {
        toast.success(
          `Successfully imported ${result.imported.length} location${result.imported.length > 1 ? 's' : ''}`,
        )
        setImported(true)

        // Trigger NOAA forecast fetch for each unique grid
        for (const grid of result.grids) {
          try {
            await triggerNoaaForecastFetch(grid.full_grid_id, grid.id)
          } catch (e) {
            console.error(`Failed to fetch forecast for grid ${grid.full_grid_id}:`, e)
          }
        }

        // Invalidate user locations to refresh the list
        await queryClient.invalidateQueries({ queryKey: ['user-locations'] })
      }
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to import locations')
    },
  })

  const toggleLocation = (name: string) => {
    const newSet = new Set(selectedLocations)
    if (newSet.has(name)) {
      newSet.delete(name)
    } else {
      newSet.add(name)
    }
    setSelectedLocations(newSet)
  }

  const toggleAll = () => {
    if (!sharedData?.customLocations) return

    if (selectedLocations.size === sharedData.customLocations.length) {
      setSelectedLocations(new Set())
    } else {
      setSelectedLocations(
        new Set(sharedData.customLocations.map((loc) => loc.name)),
      )
    }
  }

  const handleImport = () => {
    if (!user) {
      // Redirect to login with return URL
      router.navigate({
        to: '/login',
        search: { redirect: `/share/locations/${code}` },
      })
      return
    }

    if (selectedLocations.size === 0) {
      toast.error('Please select at least one location to import')
      return
    }

    importMutation.mutate({
      locationNames: Array.from(selectedLocations),
      nameOverrides,
    })
  }

  const updateLocationName = (originalName: string, newName: string) => {
    setNameOverrides((prev) => {
      if (newName === originalName) {
        // Remove override if same as original
        const updated = { ...prev }
        delete updated[originalName]
        return updated
      }
      // Keep the override even if empty (user is still typing)
      return { ...prev, [originalName]: newName }
    })
  }

  const handleGoToDashboard = () => {
    router.navigate({ to: '/dashboard' })
  }

  // Check if any selected locations have empty names
  const hasEmptyNames = () => {
    if (!sharedData?.customLocations) return false

    return Array.from(selectedLocations).some((locationName) => {
      const finalName =
        locationName in nameOverrides
          ? nameOverrides[locationName]
          : locationName
      return finalName.trim() === ''
    })
  }

  if (!sharedData || sharedData.customLocations.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              No Locations Shared
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              This share link doesn't contain any custom locations.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (imported) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-600">
              <Check className="w-5 h-5" />
              Locations Imported!
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              The selected locations have been added to your account.
            </p>
            <Button onClick={handleGoToDashboard} className="w-full">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="container max-w-2xl mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Shared Snow Spots
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              {sharedData.name || 'Someone'} shared{' '}
              {sharedData.customLocations.length} custom location
              {sharedData.customLocations.length !== 1 ? 's' : ''} with you
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {sharedData.isOneTime && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>One-time link</AlertTitle>
                <AlertDescription>
                  This link can only be used once. After you import, it will
                  expire.
                </AlertDescription>
              </Alert>
            )}

            {!user && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Sign in required</AlertTitle>
                <AlertDescription>
                  You need to sign in to add these locations to your account.
                </AlertDescription>
              </Alert>
            )}

            {/* Select All Toggle */}
            <div className="flex items-center justify-between pb-4 border-b pr-3">
              <label
                htmlFor="select-all"
                className="cursor-pointer text-sm font-medium"
              >
                Select All ({selectedLocations.size} of{' '}
                {sharedData.customLocations.length})
              </label>
              <Checkbox
                id="select-all"
                checked={
                  selectedLocations.size === sharedData.customLocations.length
                }
                onCheckedChange={toggleAll}
              />
            </div>

            {/* Location List */}
            <div className="space-y-3">
              {sharedData.customLocations.map((location) => (
                <div
                  key={location.name}
                  className="flex flex-col gap-2 p-3 rounded-lg border border-border/50 hover:border-border transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <MapPin className="w-4 h-4 mt-1 text-muted-foreground" />
                      <div className="flex-1">
                        <p className="font-medium text-sm text-muted-foreground mb-1">
                          Original: {location.name}
                        </p>
                        <Input
                          placeholder={location.name}
                          value={
                            location.name in nameOverrides
                              ? nameOverrides[location.name]
                              : location.name
                          }
                          onChange={(e) =>
                            updateLocationName(location.name, e.target.value)
                          }
                          className="h-8"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          {location.latitude.toFixed(4)},{' '}
                          {location.longitude.toFixed(4)}
                        </p>
                      </div>
                    </div>
                    <Checkbox
                      checked={selectedLocations.has(location.name)}
                      onCheckedChange={() => toggleLocation(location.name)}
                      className="ml-2"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Import Button */}
            <Button
              onClick={handleImport}
              disabled={
                importMutation.isPending ||
                selectedLocations.size === 0 ||
                hasEmptyNames()
              }
              className="w-full"
            >
              {importMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : user ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Add {selectedLocations.size} Location
                  {selectedLocations.size !== 1 ? 's' : ''} to My Spots
                </>
              ) : (
                'Sign In to Add Locations'
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
